import type { UIComponent } from '@gik/core/types/UI';
import { useBemCN } from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import DislikeIcon from '@gik/stream/assets/reactions/thumbsDown.svg';
import LikeIcon from '@gik/stream/assets/reactions/thumbsUp.svg';
import { LoadingSpinner } from '@gik/ui/LoadingSpinner';
import React from 'react';
import type { ReactionEmojiKey } from '../../../ui/OpenMoji';
import { OpenMoji } from '../../../ui/OpenMoji';

export type ReactionPickerProps = {
  selected?: string;
  loading?: boolean;
  onSelect?(name: string): void;
  onDelete?(name: string): void;
} & UIComponent;

function ReactionPickerComp({
  children,
  className,
  selected,
  loading,
  onSelect,
  onDelete,
  ...otherProps
}: React.PropsWithChildren<ReactionPickerProps>): React.ReactElement {
  const bem = useBemCN('reaction-picker');

  const reactions: ReactionEmojiKey[] = [
    'like',
    'dislike',
    'love',
    'prayers',
    'haha',
    'sad',
    'angry',
    'strong',
    'celebrate',
    'sick',
  ];

  const handleSelect = React.useCallback(
    (isSelected, name) => {
      if (isSelected) {
        return onDelete(name);
      }
      return onSelect(name);
    },
    [onDelete, onSelect]
  );

  return (
    <div {...bem(null, [{ loading }], className)} {...otherProps}>
      {loading && <LoadingSpinner center />}
      {reactions.map((name: ReactionEmojiKey) => {
        const isSelected = selected === name;
        if (name === 'like') {
          return (
            <LikeIcon
              key={name}
              height="40px"
              width="40px"
              {...bem('icon', [{ selected: isSelected }])}
              onClick={() => handleSelect(isSelected, name)}
            />
          );
        }
        if (name === 'dislike') {
          return (
            <DislikeIcon
              key={name}
              height="40px"
              width="40px"
              {...bem('icon', [{ selected: isSelected }])}
              onClick={() => handleSelect(isSelected, name)}
            />
          );
        }

        return (
          <OpenMoji
            key={name}
            name={name}
            height="40px"
            style={{ display: 'inline' }}
            onClick={() => handleSelect(isSelected, name)}
            {...bem('icon', [{ selected: isSelected }])}
          />
        );
      })}
    </div>
  );
}

export const ReactionPicker = withComponentErrorBoundary(ReactionPickerComp);
